import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type URLSearchParamsContextType = {
  clearSearchParams: () => void;
  replaceSearchParams: (params: {
    [key: string]: string | number | boolean | string[];
  }) => void;
  searchParams: URLSearchParams;
  updateSearchParams: (params: {
    [key: string]: string | number | boolean | string[];
  }) => void;
  validatedSearchParam: <T>(key: string, defaultValue: T) => T;
};

const SearchParamsContext = createContext<
  URLSearchParamsContextType | undefined
>(undefined);

type URLSearchParamsProviderProps = {
  children: ReactNode;
};

const coerceToParam = (value: string | number | boolean | string[]): string => {
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) return value.join(',');
  return value;
};

const validateFromParam = <T,>(value: string, defaultValue: T): T => {
  if (typeof defaultValue === 'number')
    return (Number(value) as unknown as T) ?? defaultValue;
  if (typeof defaultValue === 'boolean')
    return ((value === 'true') as unknown as T) ?? defaultValue;
  if (typeof defaultValue === 'string')
    return (value as unknown as T) ?? defaultValue;
  if (Array.isArray(defaultValue))
    return value
      ? (value.split(',') as unknown as T)
      : (defaultValue as unknown as T);

  return (value as unknown as T) ?? defaultValue;
};

export const URLSearchParamsProvider = ({
  children,
}: URLSearchParamsProviderProps) => {
  const [searchParams, setSearchParams] = useState<URLSearchParams>(
    new URLSearchParams(window.location?.search)
  );

  useEffect(() => {
    if (!searchParams) return;

    const url = new URL(window.location.href);
    url.search = searchParams.toString();
    window.history.replaceState({}, '', url.toString());
  }, [searchParams]);

  const updateSearchParams = (params: {
    [key: string]: string | number | boolean | string[];
  }) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      const coercedValue = coerceToParam(value);
      if (coercedValue) newSearchParams.set(key, coerceToParam(value));
      else newSearchParams.delete(key);
    });

    setSearchParams(newSearchParams);
  };

  const replaceSearchParams = (params: {
    [key: string]: string | number | boolean | string[];
  }) => {
    const newURLSearchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      const coercedValue = coerceToParam(value);
      if (coercedValue) newURLSearchParams.set(key, coerceToParam(value));
    });

    setSearchParams(newURLSearchParams);
  };

  const clearSearchParams = () => {
    setSearchParams(new URLSearchParams());
  };

  const validatedSearchParam = <T,>(key: string, defaultValue: T): T => {
    const value = searchParams.get(key);

    return validateFromParam<T>(value, defaultValue);
  };

  const contextValue = useMemo(
    () => ({
      clearSearchParams,
      replaceSearchParams,
      searchParams,
      updateSearchParams,
      validatedSearchParam,
    }),
    [
      clearSearchParams,
      replaceSearchParams,
      searchParams,
      updateSearchParams,
      validatedSearchParam,
    ]
  );

  return (
    <SearchParamsContext.Provider value={contextValue}>
      {children}
    </SearchParamsContext.Provider>
  );
};

export const useURLSearchParams = (): URLSearchParamsContextType => {
  const context = useContext(SearchParamsContext);

  if (!context) {
    throw new Error(
      'useURLSearchParams must be used within a URLSearchParamsProvider'
    );
  }

  return context;
};
